import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './LoginPage.module.css';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = params.get('token') ?? '';
  const tenantSlug = (params.get('tenant') ?? '').toLowerCase();
  const linkValid = useMemo(() => Boolean(token && tenantSlug), [token, tenantSlug]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('common:passwordMinLength', 'Password must be at least 8 characters.'));
      return;
    }
    if (password !== confirm) {
      setError(t('common:passwordsDoNotMatch', "Passwords do not match."));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Slug': tenantSlug,
        },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        console.error('[reset-password] request failed:', res.status, errBody);
        throw new Error(errBody?.detail || 'Request failed');
      }

      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      console.error('[reset-password] submit error:', err);
      setError(t('common:resetPasswordExpired', 'This reset link is invalid or has expired. Request a new one.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
        <h1 className={styles.title}>QENQ</h1>
        <p className={styles.subtitle}>
          {t('common:resetPasswordTitle', 'Choose a new password')}
        </p>

        {!linkValid ? (
          <>
            <div className={styles.error}>
              {t('common:resetPasswordTokenMissing', 'Invalid or missing reset link. Request a new one from the forgot-password page.')}
            </div>
            <Link to="/forgot-password" className={styles.linkButton}>
              {t('common:forgotPassword', 'Forgot password?')}
            </Link>
          </>
        ) : done ? (
          <>
            <div className={styles.successMessage}>
              {t('common:resetPasswordSuccess', 'Password reset. Redirecting to login...')}
            </div>
            <Link to="/login" className={styles.linkButton}>
              {t('common:backToLogin', 'Back to login')}
            </Link>
          </>
        ) : (
          <>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.field}>
              <label>{t('common:resetPasswordNew', 'New password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                required
                autoComplete="new-password"
                minLength={8}
              />
            </div>

            <div className={styles.field}>
              <label>{t('common:resetPasswordConfirm', 'Confirm new password')}</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="********"
                required
                autoComplete="new-password"
                minLength={8}
              />
            </div>

            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? '...' : t('common:resetPasswordSubmit', 'Reset password')}
            </button>

            <Link to="/login" className={styles.linkButton}>
              {t('common:backToLogin', 'Back to login')}
            </Link>
          </>
        )}
      </form>
    </div>
  );
}
